import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { type } from "arktype";
import { FileText, Link2, Loader2, X } from "lucide-react";
import { Controller, useForm, useWatch } from "react-hook-form";

import type { MedicoFilaTranscript } from "@/api/db/medico";
import type { TranscriptClass } from "@/api/db/connection";
import { Button } from "@/components/ui/button";
import { useMedicoLink } from "@/hooks/use-medico";
import { format } from "@/lib/format";
import { MedicoLabels } from "@/lib/labels/medico";
import { cn } from "@/lib/utils";

import { Modal } from "@/components/ui/modal";
import { PersonSearch, PersonSearchSchemas } from "./person-search";
import { TranscriptModal } from "./transcript-modal";

const CLASS_ORDER: TranscriptClass[] = ["first", "return", "dictation", "not_consultation"];

const FIELD_LABEL = "text-xs font-medium uppercase tracking-wide text-muted-foreground";

type Target =
	| { medPatientId: number; groupId: string }
	| { medPatientId: number }
	| { patientId: number; contactId?: string };

const transcriptClass = type.enumerated(...CLASS_ORDER);
const linkForm = type({
	useSuggestion: "boolean",
	chosen: PersonSearchSchemas.chosen.or("null"),
	class: transcriptClass,
	consent: "boolean",
	grouped: "boolean",
});

function resolveTarget({
	values,
	transcript,
}: {
	values: typeof linkForm.infer;
	transcript: MedicoFilaTranscript;
}): Target | null {
	const group = values.grouped ? transcript.groupSuggested : null;

	if (group !== null) {
		return { medPatientId: group.medPatientId, groupId: group.groupId };
	}

	if (values.useSuggestion && transcript.suggestedMedPatientId !== null) {
		return { medPatientId: transcript.suggestedMedPatientId };
	}

	if (values.chosen === null) {
		return null;
	}

	const personId =
		values.chosen.kind === "patient" ? values.chosen.patientId : values.chosen.leadId;

	if (values.chosen.sendpulseId === null) {
		return { patientId: personId };
	}

	return { patientId: personId, contactId: values.chosen.sendpulseId };
}

export function LinkModal({
	transcript,
	onClose,
}: {
	transcript: MedicoFilaTranscript;
	onClose: () => void;
}) {
	const link = useMedicoLink();
	const [reading, setReading] = useState(false);
	const { control, register, handleSubmit, setValue } = useForm<typeof linkForm.infer>({
		resolver: arktypeResolver(linkForm),
		defaultValues: {
			useSuggestion: transcript.suggestedMedPatientId !== null,
			chosen: null,
			class: transcript.transcriptClass ?? "first",
			consent: false,
			grouped: transcript.groupSuggested !== null,
		},
	});
	const [useSuggestion, chosen, selectedClass, consent, grouped] = useWatch({
		control,
		name: ["useSuggestion", "chosen", "class", "consent", "grouped"],
	});
	const current = {
		useSuggestion,
		chosen,
		class: selectedClass,
		consent,
		grouped,
	};
	const target = resolveTarget({ values: current, transcript });

	const submit = handleSubmit((values) => {
		const target = resolveTarget({ values, transcript });

		if (target === null) {
			return;
		}

		link.mutate(
			{
				transcriptId: transcript.transcriptId,
				class: values.class,
				consentRecording: values.consent,
				...target,
			},
			{ onSuccess: onClose },
		);
	});

	const groupedActive = current.grouped && transcript.groupSuggested !== null;

	const subtitle = (
		<>
			{transcript.title}
			<span className="ml-2 border-l border-border pl-2 font-mono text-xs">
				{format.dateTime(transcript.recordedAt)}
			</span>
		</>
	);

	return (
		<Modal
			title={MedicoLabels.link.title}
			description={subtitle}
			onClose={onClose}
			isPending={link.isPending}
		>
			<form onSubmit={submit} className="flex flex-col gap-4">
				<Button
					type="button"
					size="sm"
					variant="secondary"
					className="w-fit"
					onClick={() => setReading(true)}
				>
					<FileText />
					{MedicoLabels.link.read}
				</Button>

				{transcript.groupSuggested !== null && (
					<label className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
						<input {...register("grouped")} type="checkbox" className="mt-0.5 accent-primary" />
						<span className="flex flex-col gap-0.5">
							<strong className="font-semibold">Agrupar gravações</strong>
							<span className="text-muted-foreground">
								Consulta de {transcript.groupSuggested.name}
							</span>
							<span className="font-mono text-xs text-muted-foreground">
								{format.time(transcript.groupSuggested.recordedAt)}
							</span>
						</span>
					</label>
				)}

				<div className={cn("flex flex-col gap-1.5", groupedActive && "hidden")}>
					<p className={FIELD_LABEL}>{MedicoLabels.link.patientField}</p>

					{current.useSuggestion && transcript.suggestedName !== null && (
						<span className="inline-flex w-fit items-center gap-1.5 rounded-md border border-whatsapp/40 bg-whatsapp/10 px-2 py-1 text-xs font-medium text-whatsapp">
							{transcript.suggestedName}
							<span className="text-xs font-normal text-whatsapp/80">
								{MedicoLabels.link.suggestionTag}
							</span>

							<button
								type="button"
								onClick={() => setValue("useSuggestion", false)}
								aria-label="Trocar de paciente"
								className="rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-whatsapp/40"
							>
								<X className="size-3.5" />
							</button>
						</span>
					)}

					{!(current.useSuggestion && transcript.suggestedName !== null) && (
						<Controller
							control={control}
							name="chosen"
							render={({ field }) => (
								<PersonSearch
									chosen={field.value}
									onChoose={field.onChange}
									initialSearch={transcript.suggestedName ?? ""}
									disabled={link.isPending}
								/>
							)}
						/>
					)}

					{current.chosen?.kind === "lead" && (
						<p className="rounded-md border border-warning/30 bg-warning/5 px-2.5 py-1.5 text-xs text-foreground">
							{MedicoLabels.link.leadNote}
						</p>
					)}
				</div>

				<div className="flex flex-col gap-1.5">
					<label className={FIELD_LABEL} htmlFor="link-class">
						{MedicoLabels.link.classField}
					</label>

					<select
						{...register("class")}
						id="link-class"
						disabled={link.isPending}
						className="w-fit rounded-md border border-border bg-background/60 px-2.5 py-1.5 text-xs text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40"
					>
						{CLASS_ORDER.map((value) => (
							<option key={value} value={value}>
								{MedicoLabels.transcriptClass[value]}
							</option>
						))}
					</select>

					{transcript.transcriptClass !== null && (
						<p className="text-xs text-muted-foreground">{MedicoLabels.link.classHelp}</p>
					)}
				</div>

				<label className="flex items-start gap-2 text-xs text-foreground">
					<input {...register("consent")} type="checkbox" className="mt-0.5 accent-primary" />
					<span className="flex flex-col gap-0.5">
						<span>Consentimento de gravação obtido</span>
						<span className="text-xs text-muted-foreground">CFM 2.314</span>
					</span>
				</label>

				{link.isError && <p className="text-xs text-destructive">{MedicoLabels.link.error}</p>}

				<div className="mt-1 flex items-center justify-end gap-2">
					{target === null && (
						<span className="mr-auto text-xs text-muted-foreground">
							{MedicoLabels.link.needsTarget}
						</span>
					)}

					<Button
						type="button"
						size="sm"
						variant="secondary"
						disabled={link.isPending}
						onClick={onClose}
					>
						Cancelar
					</Button>

					<Button
						size="sm"
						type="submit"
						variant="default"
						className="px-4"
						disabled={target === null || link.isPending}
					>
						{link.isPending && <Loader2 className="animate-spin" />}
						{!link.isPending && <Link2 />}
						{link.isPending && MedicoLabels.link.linking}
						{!link.isPending && MedicoLabels.link.submit}
					</Button>
				</div>
			</form>

			{reading && (
				<TranscriptModal transcriptId={transcript.transcriptId} onClose={() => setReading(false)} />
			)}
		</Modal>
	);
}
